<template>
  <div>
    <data-table
      :data="shippingRequests"
      :is-hoverable="isHoverable"
      :is-focusable="isFocusable"
      :perPage="perPage"
      :total="total"
      :currentPage="currentPage"
      :isPaginated="isPaginated"
      :backEndPagination="isBackendPaginated"
      :onPageChange="onPageChange"
      :defaultSort="defaultSort"
      :defaultSortDirection="defaultSortDirection"
    >
      <template slot-scope="props">
        <b-table-column field="ref_no" label="Ref Number">{{ props.row.reference_number }}</b-table-column>

        <b-table-column
          field="shipper_declaration_date"
          label="Date of Submission"
          sortable
          defaultSort
          :visible="!activePort"
        >
          <span
            class="tag is-success"
          >{{ props.row.shipper_declaration_date | moment("DD-MM-YYYY") }}</span>
        </b-table-column>

        <b-table-column field="status" label="Name of Shipper">{{ props.row.shipper_name }}</b-table-column>

        <b-table-column field="status" label="Status" :visible="!activePort">
          <span class="tag is-warning">{{ props.row.status.split('_').join(" ") }}</span>
        </b-table-column>

        <b-table-column field="consignee_name" label="Name of Consignee" :visible="activePort">
          <span class="">{{ props.row.consignee_name }}</span>
        </b-table-column>

        <b-table-column field="ship_name" label="Name of Vessel" :visible="activePort">
          <span class="">{{ props.row.ship_name }}</span>
        </b-table-column>

        <b-table-column field="port_status" label="Port Status" :visible="activePort">
          <span class="tag is-warning">{{ props.row.port_status ? "BERTHED" : ""}}</span>
        </b-table-column>

        <b-table-column field="incoming_date" label="Expected Date of Arrival" sortable :visible="!activePort">
          <span>{{ props.row.date_of_arrival | moment("DD-MM-YYYY") }}</span>
        </b-table-column>

        <!-- ACTIONS -->
        <b-table-column field="actions" label="Actions">
          <div class="columns is-mobile">
            <!-- View the request -->
            <div class="column">
              <b-tooltip label="View the shipping application" position="is-left" multilined>
                <a class="action-item" @click.prevent="viewShippingRequest(props.row)">
                  <b-icon type="is-primary" icon="eye"></b-icon>
                  <span class="action-text">View</span>
                </a>
              </b-tooltip>
            </div>
            <!-- EOF view the request -->

            <!-- Accredit the request -->
            <div class="column" v-if="canAccreditShippingRequest">
              <b-tooltip label="Accept the shipping application" position="is-left" multilined>
                <a class="action-item" @click.prevent="onAccreditShippingRequest(props.row)">
                  <b-icon type="is-success" icon="check-circle"></b-icon>
                  <span class="action-text">Accept</span>
                </a>
              </b-tooltip>
            </div>
            <!-- EOF Acredit -->

            <!-- Reject the request -->
            <div class="column" v-if="canAccreditShippingRequest">
              <b-tooltip label="Deny the shipping application" position="is-left" multilined>
                <a class="action-item" @click.prevent="onRejectShippingRequest(props.row)">
                  <b-icon type="is-danger" icon="close-circle"></b-icon>
                  <span class="action-text">Deny</span>
                </a>
              </b-tooltip>
            </div>
            <!-- EOF Deny -->

            <!-- Approve the request -->
            <div class="column" v-if="canApproveOrDisapproveShippingRequest">
              <b-tooltip label="Approve the shipping application" position="is-left" multilined>
                <a class="action-item" @click.prevent="onApproveShippingRequest(props.row)">
                  <b-icon type="is-success" icon="check-circle"></b-icon>
                  <span class="action-text">Approve</span>
                </a>
              </b-tooltip>
            </div>
            <!-- EOF Approve -->

            <!-- DisApprove the request -->
            <div class="column" v-if="canApproveOrDisapproveShippingRequest">
              <b-tooltip label="Reject the shipping application" position="is-left" multilined>
                <a class="action-item" @click.prevent="onDisapproveShippingRequest(props.row)">
                  <b-icon type="is-danger" icon="close-circle"></b-icon>
                  <span class="action-text">Reject</span>
                </a>
              </b-tooltip>
            </div>
            <!-- EOF Reject -->
          </div>
        </b-table-column>
      </template>

      <template slot="empty">
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="alert-box" size="is-large"></b-icon>
            </p>
            <h2>No Shipping requests currently need processing.</h2>
          </div>
        </section>
      </template>
    </data-table>
    <b-loading :is-full-page="true" :active.sync="isLoading" :can-cancel="false"></b-loading>
  </div>
</template>

<script>
import DataTable from "@/modules/commonModule/components/dataTable/Main";
import ConfirmAccreditationCard from "./ConfirmCards/ConfirmAccreditationCard";
import RejectAccreditationCard from "./ConfirmCards/RejectAccreditationCard";
import ConfirmApprovalCard from "./ConfirmCards/ConfirmApprovalCard";
import ConfirmDisapprovalCard from "./ConfirmCards/ConfirmDisapprovalCard";
import ViewShippingRequestDetails from "@/modules/commonModule/components/viewShippingRequestDetails/Main";
import AccreditShippingRequestMutation from "@/modules/nimasaAgentModule/api/graphql/mutations/AccreditShippingRequest";
import ApproveOrDisapproveRequestMutation from "@/modules/nimasaAgentModule/api/graphql/mutations/ApproveOrDisapproveShippingRequest";

export default {
  name: "ShippingRequests",

  components: {
    DataTable,
    ConfirmAccreditationCard,
    RejectAccreditationCard,
    ViewShippingRequestDetails,
    ConfirmApprovalCard,
    ConfirmDisapprovalCard
  },

  props: {
    shippingRequests: { type: Array, required: true },
    canAccreditShippingRequest: { type: Boolean, default: false },
    canApproveOrDisapproveShippingRequest: { type: Boolean, default: false },
    defaultSort: { type: Array },
    defaultSortDirection: { type: String },
    isPaginated: { type: Boolean, default: true },
    isBackendPaginated: { type: Boolean, default: true },
    total: { type: Number, default: 0 },
    perPage: { type: Number, default: 10 },
    currentPage: { type: Number, default: 1 },
    activePort: {type: Boolean, default: false},
    onPageChange: {
      type: Function,
      default: () => {
        console.log("Empty onPageChange called from Shipping Request List");
      }
    }
  },

  data() {
    return {
      isHoverable: true,
      isLoading: false,
      isFocusable: false
      // currentPage: 1
    };
  },

  methods: {
    /**
     * View a shipping request's details.
     */
    viewShippingRequest: function(shippingRequest) {
      this.$modal.open({
        parent: this,
        component: ViewShippingRequestDetails,
        hasModalCard: true,
        scroll: "keep",
        props: {
          shippingRequest,
          showActivityHistory: true
        }
      });
    },

    /**
     * Display the confirmation modal when accrediting a shipping request.
     */
    onAccreditShippingRequest: function(shippingRequest) {
      this.$modal.open({
        parent: this,
        component: ConfirmAccreditationCard,
        hasModalCard: true,
        props: {
          accreditShippingRequest: this.accreditShippingRequest,
          shippingRequest
        }
      });
    },

    /**
     * Accredit a shipping request.
     */
    accreditShippingRequest: function(shippingRequest) {
      this.isLoading = true;

      this.$apollo
        .mutate({
          mutation: AccreditShippingRequestMutation,
          variables: {
            shipping_request_id: shippingRequest.id,
            type: "ACCEPT"
          }
        })
        .then(response => {
          this.$toast.open({
            duration: 3000,
            position: "is-top-right",
            message: "The shipping request has been accepted.",
            type: "is-success"
          });

          // $router.go forces the page to be reloaded so the list of shipping requests is updated.
          this.$router.go({ name: "nimasa-agent-shipping-requests" });
        })
        .catch(error => {
          this.$toast.open({
            duration: 4000,
            position: "is-top-right",
            message: "Failed to accept the shipping request. Please try again.",
            type: "is-danger"
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    /**
     * Display the modal rejecting a shipping request
     */
    onRejectShippingRequest: function(shippingRequest) {
      this.$modal.open({
        parent: this,
        component: RejectAccreditationCard,
        hasModalCard: true,
        props: {
          rejectShippingRequest: this.rejectShippingRequest,
          shippingRequest
        }
      });
    },

    /**
     * Reject a shipping request.
     */
    rejectShippingRequest: function(shippingRequest, rejectionMessage = null) {
      this.isLoading = true;

      let requestVariable = {
        shipping_request_id: shippingRequest.id,
        type: "REJECT"
      };

      if (rejectionMessage) {
        requestVariable = {
          ...requestVariable,
          rejection_message: rejectionMessage
        };
      }

      this.$apollo
        .mutate({
          mutation: AccreditShippingRequestMutation,
          variables: requestVariable
        })
        .then(response => {
          this.$toast.open({
            duration: 3000,
            position: "is-top-right",
            message: "The shipping request has been denied.",
            type: "is-success"
          });

          // $router.go forces the page to be reloaded so the list of shipping requests is updated.
          this.$router.go({ name: "nimasa-agent-shipping-requests" });
        })
        .catch(error => {
          this.$toast.open({
            duration: 4000,
            position: "is-top-right",
            message:
              "Failed to deny the shipping request. Please try again later.",
            type: "is-danger"
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    /**
     * Display the modal to approve a shipping request
     */
    onApproveShippingRequest: function(shippingRequest) {
      this.$modal.open({
        parent: this,
        component: ConfirmApprovalCard,
        hasModalCard: true,
        props: {
          approveShippingRequest: this.approveShippingRequest,
          shippingRequest
        }
      });
    },

    /**
     * Approve a shipping request.
     */
    approveShippingRequest: function(shippingRequest) {
      this.isLoading = true;

      this.$apollo
        .mutate({
          mutation: ApproveOrDisapproveRequestMutation,
          variables: {
            shipping_request_id: shippingRequest.id,
            type: "ACCEPT"
          }
        })
        .then(response => {
          this.$toast.open({
            duration: 3000,
            position: "is-top-right",
            message: "The shipping request has been approved.",
            type: "is-success"
          });

          // $router.go forces the page to be reloaded so the list of shipping requests is updated.
          this.$router.go({ name: "nimasa-agent-shipping-requests" });
        })
        .catch(error => {
          this.$toast.open({
            duration: 4000,
            position: "is-top-right",
            message:
              "Failed to approve the shipping request. Please try again.",
            type: "is-danger"
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    /**
     * Display the modal to disapprove a shipping request
     */
    onDisapproveShippingRequest: function(shippingRequest) {
      this.$modal.open({
        parent: this,
        component: ConfirmDisapprovalCard,
        hasModalCard: true,
        props: {
          disapproveShippingRequest: this.disapproveShippingRequest,
          shippingRequest
        }
      });
    },

    /**
     * Reject a shipping request.
     */
    disapproveShippingRequest: function(shippingRequest, rejectionMessage = null) {
      this.isLoading = true;

      let requestVariable = {
        shipping_request_id: shippingRequest.id,
        type: "REJECT"
      };

      if (rejectionMessage) {
        requestVariable = {
          ...requestVariable,
          rejection_message: rejectionMessage
        };
      }

      this.$apollo
        .mutate({
          mutation: ApproveOrDisapproveRequestMutation,
          variables: requestVariable
        })
        .then(response => {
          this.$toast.open({
            duration: 3000,
            position: "is-top-right",
            message: "The shipping request has been rejected.",
            type: "is-success"
          });

          // $router.go forces the page to be reloaded so the list of shipping requests is updated.
          this.$router.go({ name: "nimasa-agent-shipping-requests" });
        })
        .catch(error => {
          this.$toast.open({
            duration: 4000,
            position: "is-top-right",
            message: "Failed to reject the shipping request. Please try again.",
            type: "is-danger"
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.action-item {
  border: none;
  &:hover {
    cursor: pointer;
  }
}
</style>

